<template>
  <div
    class="variables-menu"
    :style="`transform: translateX(${barXOverflow}px)`"
    @click="onClick"
  >
    <side-bar
      class="sidebar_first"
      :class="{ 'is-hidden': hide }"
      @click="onClickFirstSidebar"
    >
      <div>
        <drawer
          v-if="features.incident"
          :title="$t('objectNames.incident', 3)"
          :open="openDrawers.incidents"
          @open="openDrawers.incidents = true"
          @close="openDrawers.incidents = false"
        >
          <button-list
            :selected-items="selectedIncidents"
            :items="incidents"
            :order="[...incidentOrder]"
            :sortable="true"
            :multi-select="true"
            @select="selectMain"
            @select-many="selectMainMany"
            @update:order="updateIncidentOrder($event)"
            @contextmenu="onIncidentContextMenu($event)"
          >
            <template #default="{ item }">
              <span>{{ item.title }}</span>
            </template>

            <template #after-list>
              <button
                key="buttonlist-add"
                type="button"
                class="button mt-1 self-start"
                @click.prevent="createIncident()"
              >
                {{ $t("labels.add") }}
              </button>
            </template>
          </button-list>
          <context-menu
            v-if="incidentContextMenu.visible"
            :x="incidentContextMenu.x"
            :y="incidentContextMenu.y"
            @close="closeIncidentContextMenu"
          >
            <template v-if="incidentContextMenu.incidents.length > 0">
              <button
                type="button"
                class="context-menu__entry"
                @click="copyItems(incidentContextMenu.incidents)"
              >
                <lucide-icon name="copy" class="align-middle" />
                {{ $t("actions.copyIncident", { count: incidentContextMenu.incidents.length }) }}
              </button>
              <button
                type="button"
                class="context-menu__entry"
                @click="duplicateIncidents(incidentContextMenu.incidents)"
              >
                <lucide-icon name="copy-plus" class="align-middle" />
                {{ $t("actions.duplicateEvent", { count: incidentContextMenu.incidents.length }) }}
              </button>
              <button
                type="button"
                class="context-menu__entry context-menu__entry--danger"
                @click="deleteIncidents(incidentContextMenu.incidents)"
              >
                <lucide-icon name="trash-2" class="align-middle" />
                {{ $t("actions.deleteIncident", { count: incidentContextMenu.incidents.length }) }}
              </button>
            </template>
            <template v-else>
              <button
                type="button"
                class="context-menu__entry"
                @click="createIncident()"
              >
                <lucide-icon name="square-plus" class="align-middle" />
                {{ $t("actions.createIncident") }}
              </button>
              <button
                :disabled="pasteableIncidents.length == 0"
                class="context-menu__entry"
                @click="pasteItems()"
              >
                <lucide-icon name="clipboard-paste" class="align-middle" />
                {{ $t("actions.paste") }}
              </button>
            </template>
          </context-menu>
        </drawer>
        <drawer
          v-if="features.action"
          :title="$t('objectNames.action', 3)"
          :open="openDrawers.actions"
          @open="openDrawers.actions = true"
          @close="openDrawers.actions = false"
        >
          <button-list
            :selected-items="selectedActions"
            :items="actions"
            :sortable="true"
            :order="[...actionOrder]"
            :multi-select="true"
            @select="selectMain"
            @select-many="selectMainMany"
            @update:order="updateActionOrder($event)"
            @contextmenu="onActionContextMenu($event)"
          >
            <template #default="{ item }">
              <span>{{ item.title }}</span>
            </template>

            <template #after-list>
              <button
                key="buttonlist-add"
                type="button"
                class="button mt-1 self-start"
                @click.prevent="createAction()"
              >
                {{ $t("labels.add") }}
              </button>
            </template>
          </button-list>

          <context-menu
            v-if="actionContextMenu.visible"
            :x="actionContextMenu.x"
            :y="actionContextMenu.y"
            @close="closeActionContextMenu"
          >
            <template v-if="actionContextMenu.actions">
              <button
                type="button"
                class="context-menu__entry"
                @click="copyItems(actionContextMenu.actions)"
              >
                <lucide-icon name="copy" class="align-middle" />
                {{ $t("actions.copyAction", { count: actionContextMenu.actions.length }) }}
              </button>
              <button
                type="button"
                class="context-menu__entry"
                @click="duplicateActions(actionContextMenu.actions)"
              >
                <lucide-icon name="copy-plus" class="align-middle" />
                {{ $t("actions.duplicateAction", { count: actionContextMenu.actions.length }) }}
              </button>
              <button
                type="button"
                class="context-menu__entry context-menu__entry--danger"
                @click="deleteActions(actionContextMenu.actions)"
              >
                <lucide-icon name="trash-2" class="align-middle" />
                {{ $t("actions.deleteAction", { count: actionContextMenu.actions.length }) }}
              </button>
            </template>
            <template v-else>
              <button
                type="button"
                class="context-menu__entry"
                @click="createAction()"
              >
                <lucide-icon name="square-plus" class="align-middle" />
                {{ $t("actions.createAction") }}
              </button>
              <button
                :disabled="pasteableActions.length == 0"
                class="context-menu__entry"
                @click="pasteItems()"
              >
                <lucide-icon name="clipboard-paste" class="align-middle" />
                {{ $t("actions.paste") }}
              </button>
            </template>
          </context-menu>
        </drawer>
        <drawer
          :title="$t('objectNames.scenario', 3)"
          :open="openDrawers.scenarios"
          @open="openDrawers.scenarios = true"
          @close="openDrawers.scenarios = false"
        >
          <button-list
            :selected-items="selectedScenarios"
            :items="scenarios"
            :order="[...scenarioOrder]"
            :sortable="true"
            :multi-select="true"
            @select="selectMain"
            @select-many="selectMainMany"
            @update:order="updateScenarioOrder($event)"
            @contextmenu="onScenarioContextMenu($event)"
          >
            <template #default="{ item }">
              <span>{{ item.title }}</span>
            </template>

            <template #after-list>
              <button
                key="buttonlist-add"
                type="button"
                class="button mt-1 self-start"
                @click.prevent="createScenario()"
              >
                {{ $t("labels.add") }}
              </button>
            </template>
          </button-list>

          <context-menu
            v-if="scenarioContextMenu.visible"
            :x="scenarioContextMenu.x"
            :y="scenarioContextMenu.y"
            @close="closeScenarioContextMenu"
          >
            <template v-if="scenarioContextMenu.scenarios.length > 0">
              <button
                type="button"
                class="context-menu__entry"
                @click="copyItems(scenarioContextMenu.scenarios)"
              >
                <lucide-icon name="copy" class="align-middle" />
                {{ $t("actions.copyScenario", { count: scenarioContextMenu.scenarios.length }) }}
              </button>
              <button
                type="button"
                class="context-menu__entry"
                @click="duplicateScenarios(scenarioContextMenu.scenarios)"
              >
                <lucide-icon name="copy-plus" class="align-middle" />
                {{ $t("actions.duplicateScenario", { count: scenarioContextMenu.scenarios.length }) }}
              </button>
              <button
                type="button"
                class="context-menu__entry context-menu__entry--danger"
                @click="deleteScenarios(scenarioContextMenu.scenarios)"
              >
                <lucide-icon name="trash-2" class="align-middle" />
                {{ $t("actions.deleteScenario", { count: scenarioContextMenu.scenarios.length }) }}
              </button>
            </template>
            <template v-else>
              <button
                type="button"
                class="context-menu__entry"
                @click="createScenario()"
              >
                <lucide-icon name="square-plus" class="align-middle" />
                {{ $t("actions.createScenario") }}
              </button>
              <button
                :disabled="pasteableScenarios.length == 0"
                class="context-menu__entry"
                @click="pasteItems()"
              >
                <lucide-icon name="clipboard-paste" class="align-middle" />
                {{ $t("actions.paste") }}
              </button>
            </template>
          </context-menu>
        </drawer>
      </div>
    </side-bar>

    <transition name="menutransition">
      <side-bar
        v-if="mainSelection != null"
        class="sidebar_second"
        :class="{ 'is-hidden': hide }"
        @click="onClickSecondSidebar"
      >
        <incident-menu
          v-if="mainSelection.type === 'incident'"
          :key="`incident-menu-${mainSelection?.id}`"
          :incident-id="mainSelection.id"
          @select="selectSub($event)"
          @deselect="deselectSubItem"
          @delete="deselectMain"
          @select-main="selectMain"
          @close="deselectMain"
          @copy="copyItem"
        />
        <action-menu
          v-if="mainSelection.type === 'action'"
          :key="`action-menu-${mainSelection?.id}`"
          :action-id="mainSelection.id"
          @select="selectSub"
          @deselect="deselectSubItem"
          @delete="deselectMain"
          @select-main="selectMain"
          @close="deselectMain"
          @copy="copyItem"
        />
        <scenario-menu
          v-if="mainSelection.type === 'scenario'"
          :key="`scenario-menu-${mainSelection?.id}`"
          :scenario-id="mainSelection.id"
          @select="selectSub"
          @deselect="deselectSubItem"
          @delete="deselectMain"
          @select-main="selectMain"
          @close="deselectMain"
          @copy="copyItem"
        />
      </side-bar>
    </transition>

    <transition name="menutransition">
      <side-bar
        v-if="subSelection != null && mainSelection != null"
        class="sidebar_third"
        :class="{ 'is-hidden': hide }"
        @click="onClickThirdSidebar"
      >
        <scenario-end-menu
          v-if="subSelection.type === 'scenarioEnd'"
          :key="`scenarioEnd-menu-${subSelection?.id}`"
          :end-id="subSelection.id"
          :sub-selection="subSubSelection"
          @select="selectSubSub($event)"
          @close="deselectSub"
        />
        <condition-menu
          v-else-if="subSelection.type === 'condition'"
          :key="`condition-menu-${subSelection?.id}`"
          :condition-id="subSelection.id"
          @close="deselectSub"
        />
        <stakeholder-menu
          v-else-if="subSelection.type === 'stakeholder'"
          :key="`stakeholder-menu-${subSelection?.id}`"
          :stakeholder-id="subSelection.id"
          @close="deselectSub"
        />
        <effect-menu
          v-else-if="subSelection.type === 'effect'"
          :key="`effect-menu-${subSelection?.id}`"
          :effect-id="subSelection.id"
          @close="deselectSub"
        />
        <trigger-menu
          v-else-if="subSelection.type === 'incidentTrigger'"
          :key="`trigger-menu-${subSelection?.id}`"
          :trigger-id="subSelection.id"
          :selected-condition="subSubSelection || undefined"
          @select="selectSubSub($event)"
          @close="deselectSub"
          @copy="copyItem"
        />
        <requirement-menu
          v-else-if="subSelection.type === 'requirement'"
          :key="`requirement-menu-${subSelection?.id}`"
          :requirement-id="subSelection.id"
          :selected-condition="subSubSelection || undefined"
          @select="selectSubSub($event)"
          @close="deselectSub"
          @copy="copyItem"
        />
      </side-bar>
    </transition>

    <transition name="menutransition">
      <side-bar
        v-if="
          mainSelection != null &&
          subSelection != null &&
          subSubSelection != null
        "
        class="sidebar_fourth"
        :class="{ 'is-hidden': hide }"
      >
        <condition-menu
          v-if="subSubSelection.type === 'condition'"
          :key="`condition-menu-${subSubSelection?.id}`"
          :condition-id="subSubSelection.id"
          @close="deselectSubSub"
        />
      </side-bar>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed, watch, ref } from "vue";
import { useCssVar, useElementSize } from "@vueuse/core";

import { useVariablesCopyPaste } from "./variablesCopyPaste";

import SideBar from "@core/ui/SideBar.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import Drawer from "@core/ui/DrawerBlock.vue";
import ContextMenu from "@core/ui/ContextMenu.vue";
import ButtonList from "@core/ui/ButtonList.vue";

// main menus
import IncidentMenu from "@editor/items/incident/IncidentMenu.vue";
import ActionMenu from "@editor/items/action/ActionMenu.vue";
import ScenarioMenu from "@editor/items/scenario/ScenarioMenu.vue";

// submenus
import ScenarioEndMenu from "@editor/items/scenarioEnd/ScenarioEndMenu.vue";
import ConditionMenu from "@editor/items/condition/ConditionMenu.vue";
import EffectMenu from "@editor/items/effect/EffectMenu.vue";
import StakeholderMenu from "@editor/items/stakeholder/StakeholderMenu.vue";
import TriggerMenu from "@editor/items/incidentTrigger/TriggerMenu.vue";
import RequirementMenu from "@editor/items/requirement/RequirementMenu.vue";

import { useFeatures } from "@editor/store/features.store";
import { useVariablesViewStore } from "@editor/views/variables/variablesView.store";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useIncidentStore } from "@editor/items/incident/incidents.store";
import { useActionStore } from "@editor/items/action/actions.store";
import { useScenarioStore } from "@editor/items/scenario/scenarios.store";
import { useModelStore } from "@editor/items/model/model.store";

import type {
  MainSelection,
  SubSelection
} from "@editor/views/variables/variablesView.store";
import type { Handleable } from "@core/types/Events";
import type { IncidentItem } from "@editor/items/incident/IncidentItem";
import type { ScenarioItem } from "@editor/items/scenario/ScenarioItem";
import type { ActionItem } from "@editor/items/action/ActionItem";
import type { ConditionItem } from "@editor/items/condition/ConditionItem";
import { notNull } from "@/helpers/utility/Type";

const props = defineProps<{
  hide: boolean;
}>();

const emit = defineEmits<{
  (event: "click", mouseEvent: MouseEvent): void;
}>();

const features = useFeatures();
const variablesView = useVariablesViewStore();
const selectionStore = useSelectionStore();
const modelStore = useModelStore();

const openDrawers = reactive({
  resources: false,
  incidents: false,
  actions: false,
  scenarios: false
});

const mainSelectedItems = computed(() => {
  return variablesView.mainSelectedItems.map(item => modelStore.getItem(item?.type, item?.id)).filter(notNull);
});

const mainSelection = computed({
  get() {
    if (!variablesView.mainSelection) return null;
    const item = variablesView.mainSelection;
    return modelStore.getItem(item?.type, item?.id);
  },
  set(item: MainSelection | null) {
    variablesView.selectMain(item);
    selectionStore.deselectStakeholders();
  }
});

const subSelection = computed({
  get() {
    if (!variablesView.subSelection) return null;
    const item = variablesView.subSelection;
    return modelStore.getItem(item?.type, item?.id);
  },
  set(item: SubSelection | null) {
    variablesView.selectSub(item);
    if (item == null) {
      selectionStore.deselectStakeholders();
    }
  }
});

const subSubSelection = computed({
  get() {
    if (!variablesView.subSubSelection) return null;
    const item = variablesView.subSubSelection;
    return modelStore.getItem(item?.type, item?.id);
  },
  set(item: ConditionItem | null) {
    variablesView.selectSubSub(item);
  }
});

watch(
  mainSelection,
  (item) => {
    if (item == null) return;
    if (item.type === "incident") openDrawers.incidents = true;
    if (item.type === "action") openDrawers.actions = true;
    if (item.type === "scenario") openDrawers.scenarios = true;
  },
  { immediate: true }
);

function selectMain(item: MainSelection | null) {
  variablesView.selectMain(item);
}
function selectMainMany(items: MainSelection[]) {
  variablesView.selectMainMany(items);
}
function deselectMain() {
  variablesView.selectMain(null);
}
function selectSub(item: SubSelection | null) {
  variablesView.selectSub(item);
}
function deselectSub(mouseEvent?: MouseEvent & Handleable) {
  if (mouseEvent != null && mouseEvent.hasBeenHandled) return;
  variablesView.selectSub(null);
}
function deselectSubItem(item: { id: number }) {
  if (subSelection.value?.id === item?.id) {
    variablesView.selectSub(null);
  }
}

function selectSubSub(item: { type: "condition"; id: number } | null) {
  variablesView.selectSubSub(item);
}
function deselectSubSub(mouseEvent?: MouseEvent & Handleable) {
  if (mouseEvent != null && mouseEvent.hasBeenHandled) return;
  variablesView.selectSubSub(null);
}

function onClickFirstSidebar(mouseEvent: MouseEvent & Handleable) {
  if (props.hide) return;
  if (mouseEvent.defaultPrevented) return;
  if (mouseEvent.hasBeenHandled) return;
  mouseEvent.hasBeenHandled = true;
  deselectMain();
}
function onClickSecondSidebar(mouseEvent: MouseEvent & Handleable) {
  if (props.hide) return;
  if (mouseEvent.defaultPrevented) return;
  if (mouseEvent.hasBeenHandled) return;
  mouseEvent.hasBeenHandled = true;
  deselectSub();
}
function onClickThirdSidebar(mouseEvent: MouseEvent & Handleable) {
  if (props.hide) return;
  if (mouseEvent.defaultPrevented) return;
  if (mouseEvent.hasBeenHandled) return;
  mouseEvent.hasBeenHandled = true;
  deselectSubSub();
}
function onClick(mouseEvent: MouseEvent) {
  emit("click", mouseEvent);
}

// ==============================
// INCIDENT MENU

const incidentStore = useIncidentStore();

const incidents = computed(() => {
  return incidentStore.items;
});

const selectedIncidents = computed(() => {
  return mainSelectedItems.value.filter(item => item.type === "incident");
});

async function createIncident() {
  const newIncident = await incidentStore.create();
  selectMain(newIncident);
}
async function deleteIncidents(incidents: IncidentItem[]) {
  incidentContextMenu.incidents = [];
  await incidentStore.deleteMany(incidents);
}
async function duplicateIncidents(incidents: IncidentItem[]) {
  const newIncidents = await Promise.all(incidents.map(incident => incidentStore.duplicate(incident.id)));
  selectMainMany(newIncidents);
}

const incidentOrder = computed(() => {
  return incidentStore.order;
});
function updateIncidentOrder(order: number[]) {
  incidentStore.saveOrder(order);
}

const incidentContextMenu = reactive({
  visible: false,
  x: 0,
  y: 0,
  incidents: [] as IncidentItem[]
});
function onIncidentContextMenu(event: MouseEvent & { items: IncidentItem[] }) {
  incidentContextMenu.x = event.clientX;
  incidentContextMenu.y = event.clientY;
  incidentContextMenu.visible = true;
  incidentContextMenu.incidents = event.items;
  if (event.items.length === 0) {
    getPasteableItems();
  }
}
function closeIncidentContextMenu() {
  incidentContextMenu.visible = false;
  incidentContextMenu.incidents = [];
}

const pasteableIncidents = computed(() => {
  return pasteableItems.value.filter((item) => item.type === "incident");
});

// ==============================
// ACTION MENU

const actionStore = useActionStore();

const actions = computed(() => {
  return actionStore.items;
});

const selectedActions = computed(() => {
  return mainSelectedItems.value.filter(item => item.type === "action");
});

async function deleteActions(actions: ActionItem[]) {
  actionContextMenu.actions = [];
  await actionStore.deleteMany(actions);
}
async function createAction() {
  const newAction = await actionStore.create();
  selectMain(newAction);
}
async function duplicateActions(actions: ActionItem[]) {
  const newActions = await Promise.all(actions.map(action => actionStore.duplicate(action.id)));
  selectMainMany(newActions);
}

const actionOrder = computed(() => {
  return actionStore.order;
});
function updateActionOrder(order: number[]) {
  actionStore.saveOrder(order);
}

const actionContextMenu = reactive({
  visible: false,
  x: 0,
  y: 0,
  actions: [] as ActionItem[]
});
function onActionContextMenu(event: MouseEvent & { items: ActionItem[] }) {
  actionContextMenu.x = event.clientX;
  actionContextMenu.y = event.clientY;
  actionContextMenu.visible = true;
  actionContextMenu.actions = event.items;
  if (event.items.length === 0) {
    getPasteableItems();
  }
}
function closeActionContextMenu() {
  actionContextMenu.visible = false;
  actionContextMenu.actions = [];
}

const pasteableActions = computed(() => {
  return pasteableItems.value.filter((item) => item.type === "action");
});

// ==============================
// SCENARIO MENU

const scenarioStore = useScenarioStore();

const scenarios = computed(() => {
  return scenarioStore.items;
});

const selectedScenarios = computed(() => {
  return mainSelectedItems.value.filter(item => item.type === "scenario");
});

async function deleteScenarios(scenarios: ScenarioItem[]) {
  scenarioContextMenu.scenarios = [];
  await scenarioStore.deleteMany(scenarios);
}
async function createScenario() {
  const newScenario = await scenarioStore.create();
  selectMain(newScenario);
}
async function duplicateScenarios(scenarios: ScenarioItem[]) {
  const newScenarios = await Promise.all(scenarios.map(scenario => scenarioStore.duplicate(scenario.id)));
  selectMainMany(newScenarios);
}

const scenarioOrder = computed(() => {
  return scenarioStore.order;
});
function updateScenarioOrder(order: number[]) {
  scenarioStore.saveOrder(order);
}

const scenarioContextMenu = reactive({
  visible: false,
  x: 0,
  y: 0,
  scenarios: [] as ScenarioItem[]
});
function onScenarioContextMenu(event: MouseEvent & { items: ScenarioItem[] }) {
  scenarioContextMenu.x = event.clientX;
  scenarioContextMenu.y = event.clientY;
  scenarioContextMenu.visible = true;
  scenarioContextMenu.scenarios = event.items;
  if (event.items.length === 0) {
    getPasteableItems();
  }
}
function closeScenarioContextMenu() {
  scenarioContextMenu.visible = false;
  scenarioContextMenu.scenarios = [];
}

const pasteableScenarios = computed(() => {
  return pasteableItems.value.filter((item) => item.type === "scenario");
});

// ==============================
// COPY/PASTE

const { copyItem, copyItems, registerPasteEvent, getPasteableItems, pasteItems, pasteableItems } = useVariablesCopyPaste();
registerPasteEvent();

// ==============================
// SCREEN WIDTH MANAGEMENT

const barWidth = useCssVar("--sidebar-width");
const rem = ref(14);
const barWidthPX = computed(() => {
  return (parseInt(barWidth.value ?? "") || 22) * rem.value;
});
const allBarsWidth = computed(() => {
  if (subSubSelection.value != null) return barWidthPX.value * 4;
  if (subSelection.value != null) return barWidthPX.value * 3;
  if (mainSelection.value != null) return barWidthPX.value * 2;
  return barWidthPX.value;
});
const screenSize = useElementSize(document.documentElement);
const barXOverflow = computed(() => {
  if (screenSize.width.value <= 0) return 0;
  return Math.min(screenSize.width.value - allBarsWidth.value, 0);
});
</script>

<style lang="scss">
.variables-menu {
  position: relative;
  transition: transform 200ms;
  z-index: 1;
}

// transition
/* Enter and leave animations can use different */
/* durations and timing functions.              */
.menutransition-enter-active {
  transition: all 100ms ease-out;
}
.menutransition-leave-active {
  transition: all 200ms ease-in;
}
.menutransition-enter-from,
.menutransition-leave-to {
  transform: translate3d(-100%, 0, 0);
}
</style>
